import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import api from '../../services/api';

const ExportDataModal = ({ isOpen, onClose, user }) => {
  const { isDark } = useTheme();
  const [batches, setBatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [includeAnalyticsMd, setIncludeAnalyticsMd] = useState(true);
  const [includeAnalyticsCsv, setIncludeAnalyticsCsv] = useState(true);
  const [includeRawResults, setIncludeRawResults] = useState(true);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && user) {
      fetchUserBatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  const fetchUserBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/admin/users/${user.id}/batches`);
      setBatches(response.data.batches);
      // Pre-select all completed batches
      const completedBatches = response.data.batches
        .filter(b => b.status === 'completed')
        .map(b => b.id);
      setSelectedBatches(completedBatches);
    } catch (err) {
      console.error('Failed to fetch batches:', err);
      setError('Failed to load user batches');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchToggle = (batchId) => {
    setSelectedBatches(prev => {
      if (prev.includes(batchId)) {
        return prev.filter(id => id !== batchId);
      }
      return [...prev, batchId];
    });
  };

  const handleSelectAll = () => {
    if (selectedBatches.length === batches.length) {
      setSelectedBatches([]);
    } else {
      setSelectedBatches(batches.map(b => b.id));
    }
  };

  const handleExport = async () => {
    if (selectedBatches.length === 0) {
      alert('Please select at least one batch to export');
      return;
    }

    setExporting(true);
    try {
      // Build URL with batch IDs
      const batchIdsParam = selectedBatches.join(',');
      const url = `/admin/users/${user.id}/export-executions?batchIds=${batchIdsParam}`;
      
      // Make request with blob response type
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5555/api'}${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get the blob
      const blob = await response.blob();
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `export_${user.email.split('@')[0]}_${new Date().toISOString().slice(0, 10)}.zip`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      // Close modal after successful export
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className={`glass-modal w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col ${isDark ? 'text-white' : 'text-gray-800'}`}>
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <h2 className="text-2xl font-bold heading-mono">Export Data</h2>
          <p className="text-sm mt-2 opacity-80">
            Export execution data for: <span className="font-mono">{user?.email}</span>
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="glass-spinner w-8 h-8 mx-auto mb-4"></div>
              <p className="text-mono">Loading batches...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-400">{error}</p>
              <button 
                onClick={fetchUserBatches}
                className="btn-glass mt-4 px-4 py-2"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Batch Selection */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold heading-mono">Select Execution Batches</h3>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm btn-glass px-3 py-1"
                  >
                    {selectedBatches.length === batches.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {batches.length === 0 ? (
                    <p className="text-center py-4 opacity-60">No execution batches found for this user</p>
                  ) : (
                    batches.map(batch => (
                      <label
                        key={batch.id}
                        className={`flex items-start p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedBatches.includes(batch.id) 
                            ? 'bg-blue-500/20 border border-blue-500/40' 
                            : 'bg-white/5 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedBatches.includes(batch.id)}
                          onChange={() => handleBatchToggle(batch.id)}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{batch.name}</div>
                          <div className="text-sm opacity-70 mt-1">
                            <span className="mr-4">Date: {formatDate(batch.started_at)}</span>
                            <span className="mr-4">Queries: {batch.total_queries}</span>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              batch.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                              batch.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                              batch.status === 'running' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {batch.status}
                            </span>
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Export Options */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold heading-mono mb-4">Include in Export</h3>
                <div className="space-y-2">
                  <label className="flex items-center p-2 rounded hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeAnalyticsMd}
                      onChange={(e) => setIncludeAnalyticsMd(e.target.checked)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">Analytics Report (Markdown)</div>
                      <div className="text-sm opacity-60">Comprehensive analytics in readable format</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-2 rounded hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeAnalyticsCsv}
                      onChange={(e) => setIncludeAnalyticsCsv(e.target.checked)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">Analytics Data (CSV)</div>
                      <div className="text-sm opacity-60">Data in spreadsheet format</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-2 rounded hover:bg-white/5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeRawResults}
                      onChange={(e) => setIncludeRawResults(e.target.checked)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">Raw Search Results (Markdown)</div>
                      <div className="text-sm opacity-60">Complete AI responses with citations</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Selected Summary */}
              {selectedBatches.length > 0 && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <p className="text-sm">
                    <strong>{selectedBatches.length}</strong> batch{selectedBatches.length !== 1 ? 'es' : ''} selected
                    {' • '}
                    <strong>
                      {batches
                        .filter(b => selectedBatches.includes(b.id))
                        .reduce((sum, b) => sum + b.total_queries, 0)}
                    </strong> total queries
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={exporting}
            className="btn-glass px-6 py-2 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || loading || selectedBatches.length === 0}
            className="btn-glass-accent px-6 py-2 disabled:opacity-50"
          >
            {exporting ? 'Exporting...' : 'Export Selected'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDataModal;